/**
 * A module's own actions and checks, as its manifest declares them
 * (admin.actions / admin.checks). Platform code: it shows and runs whatever
 * the module declares and knows nothing about any module.
 *
 *   const { actions, checks, run, checkLeave } = useModuleFlowActions(instanceId, stepId, { withChecks });
 *
 * `actions` carry their latest job; while one is queued or running the list is
 * re-read every two seconds so the button shows progress, and `checks` (the
 * evaluated gate checks, read with the actions when `withChecks`) with it.
 * `checkLeave()` returns the unmet gate checks, for the warning before Seuraava.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import API_ENDPOINTS from '../config/api';

const ACTIVE = new Set(['queued', 'running']);

async function readJson(res) {
  const data = await res.json().catch(() => null);
  if (!res.ok || data?.success === false) {
    const err = new Error(data?.message || data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export default function useModuleFlowActions(instanceId, stepId, { withChecks = false } = {}) {
  const [actions, setActions] = useState([]);
  // The step's gate checks, evaluated (withChecks only): the flow bar's ✓ / ⚠ (#1217).
  const [checks, setChecks] = useState([]);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!instanceId || !stepId) { setActions([]); setChecks([]); return []; }
    try {
      const opts = { credentials: 'include' };
      const [data, checkData] = await Promise.all([
        readJson(await fetch(API_ENDPOINTS.CHAIN_STEP_ADMIN_ACTIONS(instanceId, stepId), opts)),
        withChecks ? readJson(await fetch(API_ENDPOINTS.CHAIN_STEP_CHECKS(instanceId, stepId), opts)) : null,
      ]);
      setActions(data.actions || []);
      if (checkData) setChecks((checkData.checks || []).filter((c) => c.gate));
      setError(null);
      return data.actions || [];
    } catch (err) {
      // Not signed in with a Sitra account: no buttons, and say why.
      setActions([]);
      setChecks([]);
      setError(err.status === 401 || err.status === 403 ? 'signin' : err.message);
      return [];
    }
  }, [instanceId, stepId, withChecks]);

  useEffect(() => { load(); }, [load]);

  // Poll only while something runs.
  useEffect(() => {
    clearTimeout(timer.current);
    if (actions.some((a) => ACTIVE.has(a.latestJob?.status))) {
      timer.current = setTimeout(load, 2000);
    }
    return () => clearTimeout(timer.current);
  }, [actions, load]);

  /** Start an action; `params` are its run-time choices (manifest params), if any. */
  const run = useCallback(async (actionId, params = {}) => {
    await readJson(await fetch(API_ENDPOINTS.CHAIN_STEP_ADMIN_ACTION(instanceId, stepId, actionId), {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ params }),
    }));
    return load();
  }, [instanceId, stepId, load]);

  /** Unmet gate checks for the live step; [] when there are none or they cannot be read. */
  const checkLeave = useCallback(async () => {
    if (!instanceId || !stepId) return [];
    try {
      const data = await readJson(await fetch(API_ENDPOINTS.CHAIN_STEP_CHECKS(instanceId, stepId), { credentials: 'include' }));
      return (data.checks || []).filter((c) => c.gate && !c.satisfied);
    } catch (err) {
      // A check that cannot be read must not silently wave the facilitator
      // through: report it as an unmet check with the reason.
      return [{ id: 'unreadable', satisfied: false, detail: err.message, labels: null, action: null }];
    }
  }, [instanceId, stepId]);

  return { actions, checks, error, run, reload: load, checkLeave };
}
